import React, { useEffect, useState } from "react";
import { getCountries } from "../../../api/location/getCountries";
import { getRegions } from "../../../api/location/getRegions";
import { getDistricts } from "../../../api/location/getDistricts";
import { getLocalizedName } from "../../../api/location/locationUtils";
import { useTranslation } from "react-i18next";

import addIcon from '../../../assets/icons/add.svg';
import deleteIcon from '../../../assets/icons/delete.svg';
import warning_icon from '../../../assets/icons/WarningCircle.svg';
import phoneIcon from '../../../assets/icons/phone.svg';
import locationIcon from '../../../assets/icons/marker.svg';
import userIcon from '../../../assets/icons/user.svg';

export function ContactInfo({
    userData = {},
    additionalPhones = [],
    phoneErrors = [],
    handlePhoneChange = () => { },
    handleDeletePhone = () => { },
    handleAddPhone = () => { },
    UserLocations = [],
    selectedLocation = null,
    handleSelectLocation = () => { },
    isSubmitted = false,
}) {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const [countries, setCountries] = useState([]);
    const [regions, setRegions] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [newPhone, setNewPhone] = useState('');

    useEffect(() => {
        const loadCountries = async () => {
            try {
                const data = await getCountries();
                setCountries(data);
            } catch (error) {
                console.error("Failed to load countries:", error);
            }
        };
        loadCountries();
    }, []);

    const handleCountryChange = async (e) => {
        const value = e.target.value;
        if (!value) {
            setSelectedCountry(null);
            setSelectedRegion(null);
            setSelectedDistrict(null);
            setRegions([]);
            setDistricts([]);
            return;
        }

        const id = parseInt(value, 10);
        const country = countries.find((c) => c.id === id) || null;
        setSelectedCountry(country);
        setSelectedRegion(null);
        setSelectedDistrict(null);
        setRegions([]);
        setDistricts([]);

        if (country) {
            try {
                const regs = await getRegions(country.id);
                setRegions(regs);
            } catch (error) {
                console.error("Failed to load regions:", error);
            }
        }
    };

    const handleRegionChange = async (e) => {
        const value = e.target.value;
        if (!value) {
            setSelectedRegion(null);
            setSelectedDistrict(null);
            setDistricts([]);
            return;
        }

        const id = parseInt(value, 10);
        const region = regions.find((r) => r.id === id) || null;
        setSelectedRegion(region);
        setSelectedDistrict(null);
        setDistricts([]);

        if (region) {
            try {
                const dists = await getDistricts(region.id);
                setDistricts(dists);
            } catch (error) {
                console.error("Failed to load districts:", error);
            }
        }
    };

    const handleDistrictChange = (e) => {
        const value = e.target.value;
        if (!value) {
            setSelectedDistrict(null);
            handleSelectLocation(null);
            return;
        }

        const id = parseInt(value, 10);
        const district = districts.find((d) => d.id === id) || null;
        setSelectedDistrict(district);

        if (district) {
            const syntheticLocation = {
                country_id: selectedCountry?.id || null,
                region_id: selectedRegion?.id || null,
                district_id: district.id,
                country: getLocalizedName(selectedCountry, currentLanguage),
                province: getLocalizedName(selectedRegion, currentLanguage),
                locality: getLocalizedName(district, currentLanguage),
            };
            handleSelectLocation(syntheticLocation);
        }
    };

    const handleAddNewPhone = () => {
        if (newPhone.trim()) {
            handleAddPhone();
            handlePhoneChange(additionalPhones.length, newPhone.trim());
            setNewPhone('');
        }
    };

    const hasAddress = !!(selectedCountry || selectedRegion || selectedDistrict || selectedLocation);

    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#147F26]/10 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-[#147F26]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-[#147F26]">{t('contactInfo')}</h3>
                </div>
                <p className="text-sm text-[#7AB30E] mt-2">{t('contactInfoHint')}</p>
            </div>

            <div className="p-6 space-y-6">
                {/* User Info */}
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#147F26]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <img src={userIcon} alt="" className="w-5 h-5 text-[#147F26]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <label className="block text-sm font-medium text-[#147F26] mb-1.5">{t('fullName')}</label>
                        <input
                            type="text"
                            value={userData?.name || ''}
                            readOnly
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700"
                        />
                    </div>
                </div>

                {/* Phone Numbers */}
                <div>
                    <label className="block text-sm font-medium text-[#147F26] mb-3">{t('phoneNumbers')}</label>

                    {/* Main Phone */}
                    <div className="flex items-center gap-3 p-3 bg-[#147F26]/5 rounded-xl mb-3">
                        <img src={phoneIcon} alt="" className="w-5 h-5 text-[#147F26]" />
                        <span className="font-medium text-gray-900">{userData?.phone_number || t('noPhone')}</span>
                        <span className="ml-auto text-xs text-[#147F26] font-medium">{t('primary')}</span>
                    </div>

                    {/* Additional Phones */}
                    {additionalPhones?.map((phone, index) => (
                        <div key={index} className="flex items-center gap-2 mb-2">
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => handlePhoneChange(index, e.target.value)}
                                placeholder={t('additionalPhone')}
                                className={`flex-1 px-4 py-2.5 border rounded-xl outline-none transition-all ${
                                    phoneErrors[index]
                                        ? 'border-red-400 focus:border-red-400 bg-red-50'
                                        : 'border-gray-200 focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20'
                                }`}
                            />
                            <button
                                onClick={() => handleDeletePhone(index)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                            >
                                <img src={deleteIcon} alt={t('delete')} className="w-5 h-5" />
                            </button>
                        </div>
                    ))}

                    {/* Add Phone Input */}
                    <div className="flex items-center gap-2 mt-3">
                        <input
                            type="tel"
                            value={newPhone}
                            onChange={(e) => setNewPhone(e.target.value)}
                            placeholder={t('newPhone')}
                            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all"
                        />
                        <button
                            onClick={handleAddNewPhone}
                            disabled={!newPhone.trim()}
                            className="p-2.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 rounded-xl transition-colors"
                        >
                            <img src={addIcon} alt={t('add')} className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Location Section */}
                <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-start gap-3 mb-4">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <div className="w-10 h-10 bg-[#147F26]/10 rounded-full flex items-center justify-center"><img src={locationIcon} alt="" className="w-5 h-5 text-[#147F26]" /></div>
                        </div>
                        <div>
                            <h4 className="font-medium text-[#147F26]">{t('adLocation')}</h4>
                            <p className="text-sm text-[#7AB30E]">{t('adLocationHint')}</p>
                        </div>
                    </div>

                    {isSubmitted && !hasAddress && (
                        <div className="flex items-center gap-2 mb-4 p-3 bg-red-50 rounded-xl text-red-600 text-sm">
                            <img src={warning_icon} alt="" className="w-4 h-4" />
                            <span>{t('selectRegionError')}</span>
                        </div>
                    )}

                    <div className="space-y-3">
                        {/* Country */}
                        <div>
                            <label className="block text-xs font-medium text-[#147F26] mb-1.5">{t('country')}</label>
                            <select
                                value={selectedCountry?.id || ""}
                                onChange={handleCountryChange}
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all appearance-none cursor-pointer"
                                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.5rem' }}
                            >
                                <option value="">{t('selectCountry')}</option>
                                {countries.map((country) => (
                                    <option key={country.id} value={country.id}>
                                        {getLocalizedName(country, currentLanguage)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Region */}
                        <div>
                            <label className="block text-xs font-medium text-[#147F26] mb-1.5">{t('region')}</label>
                            <select
                                value={selectedRegion?.id || ""}
                                onChange={handleRegionChange}
                                disabled={!selectedCountry}
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400"
                                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.5rem' }}
                            >
                                <option value="">{t('selectRegion')}</option>
                                {regions.map((region) => (
                                    <option key={region.id} value={region.id}>
                                        {getLocalizedName(region, currentLanguage)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* District */}
                        <div>
                            <label className="block text-xs font-medium text-[#147F26] mb-1.5">{t('district')}</label>
                            <select
                                value={selectedDistrict?.id || ""}
                                onChange={handleDistrictChange}
                                disabled={!selectedRegion}
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400"
                                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.5rem' }}
                            >
                                <option value="">{t('selectDistrict')}</option>
                                {districts.map((district) => (
                                    <option key={district.id} value={district.id}>
                                        {getLocalizedName(district, currentLanguage)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
